import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import { Icon, type IconName } from './Icon.js';
import { useNavBadges } from '../hooks/useForms.js';
import { usePendingSignupRequests } from '../hooks/usePlatformAdmin.js';
import { useSession } from '../contexts/SessionContext.js';
import type { AccountModuleKey } from '@evo-hub/shared';

// `module` (2026-08-17): cuál módulo tiene que estar habilitado para que este
// ítem exista. Sin `module` = siempre (Home y Ajustes no se apagan nunca).
// Esconder el ítem es SOLO cosmética — la ruta sigue existiendo en App.tsx y
// el 403 del backend es el gate real; sacarlo del menú es para no ofrecer algo
// que no funciona, no para proteger nada.
const NAV_ITEMS: Array<{ to: string; labelKey: string; icon: IconName; module?: AccountModuleKey }> = [
  { to: '/', labelKey: 'nav.home', icon: 'home' },
  { to: '/email', labelKey: 'nav.email', icon: 'email', module: 'email' },
  { to: '/leads', labelKey: 'nav.leads', icon: 'leads', module: 'leads' },
  { to: '/embudos', labelKey: 'nav.funnels', icon: 'funnel', module: 'funnels' },
  { to: '/contacts', labelKey: 'nav.contacts', icon: 'contacts', module: 'contacts' },
  { to: '/formularios', labelKey: 'nav.forms', icon: 'forms', module: 'forms' },
  { to: '/proyectos', labelKey: 'nav.projects', icon: 'projects', module: 'projects' },
  { to: '/reports', labelKey: 'nav.reports', icon: 'reports', module: 'reports' },
  { to: '/tasks', labelKey: 'nav.tasks', icon: 'tasks', module: 'tasks' },
  { to: '/mensajes', labelKey: 'nav.messages', icon: 'messages', module: 'messaging' },
  { to: '/facturacion', labelKey: 'nav.facturacion', icon: 'billing', module: 'billing' },
  { to: '/settings', labelKey: 'nav.settings', icon: 'settings' },
];

const PLATFORM_ADMIN_ITEM: { to: string; labelKey: string; icon: IconName } =
  { to: '/admin/signup-requests', labelKey: 'nav.platformAdmin', icon: 'admin' };

// Panel de plataforma (2026-08-17) — separado de las solicitudes de registro:
// aprobar un alta y administrar las cuentas ya creadas son dos trabajos
// distintos, y meterlos en una sola pantalla obligaría a elegir cuál de los dos
// manda en la vista.
const PLATFORM_ACCOUNTS_ITEM: { to: string; labelKey: string; icon: IconName } =
  { to: '/admin/cuentas', labelKey: 'nav.platformAccounts', icon: 'accounts' };

// Catálogo de planes (2026-08-19) — pantalla propia y no una pestaña dentro de
// cuentas: los planes son de la plataforma, no de ninguna cuenta en particular.
// Meterlos adentro del detalle de una cuenta reproduciría justo el modelo que
// este trabajo vino a reemplazar.
const PLATFORM_PLANS_ITEM: { to: string; labelKey: string; icon: IconName } =
  { to: '/admin/planes', labelKey: 'nav.platformPlans', icon: 'plans' };

interface SidebarProps {
  collapsed: boolean;
}

export function Sidebar({ collapsed }: SidebarProps) {
  const { t } = useTranslation();
  const { isPlatformAdmin, modules } = useSession();
  // Un solo endpoint para todos los indicadores del menú.
  const { data: badges } = useNavBadges();
  const { data: pendingData } = usePendingSignupRequests(isPlatformAdmin);
  const pendingCount = pendingData?.requests.length ?? 0;

  // Qué módulo muestra qué. El badge cuenta lo que está PENDIENTE y todavía no
  // viste; entrar al módulo lo apaga. La fila sigue marcada en la lista hasta
  // que actúes: el número deja de gritar, el trabajo pendiente no se pierde.
  const badgePorRuta: Record<string, number> = {
    '/email': badges?.email ?? 0,
    '/leads': badges?.leads ?? 0,
    '/embudos': badges?.funnels ?? 0,
    '/formularios': badges?.forms ?? 0,
    '/proyectos': badges?.projects ?? 0,
    '/reports': badges?.reports ?? 0,
    '/tasks': badges?.tasks ?? 0,
    '/mensajes': badges?.messages ?? 0,
    '/facturacion': badges?.billing ?? 0,
    [PLATFORM_ADMIN_ITEM.to]: pendingCount,
  };

  const visibleItems = NAV_ITEMS.filter(item => item.module == null || modules[item.module]);
  const navItems = isPlatformAdmin
    ? [...visibleItems, PLATFORM_ADMIN_ITEM, PLATFORM_ACCOUNTS_ITEM, PLATFORM_PLANS_ITEM]
    : visibleItems;

  return (
    <aside
      className="flex flex-col shrink-0"
      style={{
        width: collapsed ? '80px' : '200px',
        background: 'transparent',
        borderRight: '1px solid var(--evo-stroke)',
        transition: 'width 180ms ease',
        overflow: 'hidden',
      }}
    >
      {/* Scroll propio: el aside corta con overflow hidden, y en una pantalla
          baja (sobre todo con los ítems de plataforma) los últimos quedaban
          cortados en vez de alcanzables. */}
      <nav className="flex-1 px-3 py-4 space-y-1" style={{ overflowY: 'auto', minHeight: 0 }}>
        {navItems.map(({ to, labelKey, icon }) => {
          const badgeCount = badgePorRuta[to] ?? 0;
          return (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              title={collapsed ? t(labelKey) : undefined}
              className={({ isActive }) => clsx('nav-link', isActive && 'active')}
              style={collapsed ? { justifyContent: 'center', gap: 0, paddingLeft: 0, paddingRight: 0, position: 'relative' } : {}}
            >
              <Icon name={icon} size={18} />
              {!collapsed && (
                <span style={{ fontFamily: 'var(--evo-font)', flex: 1 }}>{t(labelKey)}</span>
              )}
              {badgeCount > 0 && !collapsed && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 'var(--evo-badge-min-w)', height: 'var(--evo-pill-h-xs)', padding: '0 var(--evo-badge-px)',
                  borderRadius: 'var(--evo-badge-r)', fontSize: 'var(--evo-badge-fs)', fontWeight: 'var(--evo-badge-fw)',
                  background: 'var(--evo-cyan)', color: 'var(--evo-fg-on-cyan)',
                  fontFamily: 'var(--evo-font)', lineHeight: 1, flexShrink: 0, boxSizing: 'border-box',
                }}>
                  {/* Tope visual: un badge de cuatro cifras rompe la fila y
                      además ya no informa nada — 861 y 900 se leen igual. */}
                  {badgeCount > 99 ? '99+' : badgeCount}
                </span>
              )}
              {badgeCount > 0 && collapsed && (
                <span style={{
                  position: 'absolute', top: '4px', right: '4px',
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: 'var(--evo-cyan)',
                }} />
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
